import React from 'react';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { Product } from '@portal/portal-core';
import { PRODUCT_INFO } from 'constants/products';
import { FjButton } from 'components/primitives';

import FjuLogo from './images/flowjo-university-logo.svg';
import FjuMainImage from './images/fju-main-image.png';

const { NAMES_UI_TABS } = Product;

interface FJUDrawerProps {
  theme: DefaultTheme;
}

const FJUDrawer = ({ theme }: FJUDrawerProps): React.JSX.Element => {
  const loadFJU = (productUrlString: string): void => {
    const fjuUrl = 'https://www.flowjo.com/learn/flowjo-university';
    const fjuProductUrl = fjuUrl.concat(productUrlString);
    window.open(fjuProductUrl);
  };

  const buttonProps = {
    defaultStyle: {
      background: theme.colors.selectableLightBg,
      color: theme.colors.white,
      borderColor: theme.colors.selectableLightBg,
    },
    hoverStyle: {
      background: theme.colors.selectableRollover,
      color: theme.colors.white,
      borderColor: theme.colors.selectableRollover,
    },
    width: '120px',
    height: '35px',
  };
  const flowjoLogo = PRODUCT_INFO[NAMES_UI_TABS.flowJo].logoSvg ?? undefined;
  const seqgeqLogo = PRODUCT_INFO[NAMES_UI_TABS.seqGeq].logoSvg ?? undefined;

  return (
    <Container>
      <HeaderImage src={FjuLogo} alt="Flowjo University Logo" />
      <SubHeader>FlowJo University. Committed to Excellence.</SubHeader>
      <UpperText>
        Our goal is to expand and share knowledge, enable discovery, and elevate your ability to
        conduct trailblazing research one cell at a time.
      </UpperText>
      <MainImage src={FjuMainImage} alt="laptop, backpack, and notepad laid out on grass" />
      <Text>
        FlowJo University is a hub for free introductory & advanced online resources, with new
        content & recorded webinars posted monthly.
      </Text>
      <PanelContainer>
        <Panel>
          <FlowJoLogoContainer src={flowjoLogo} alt="FlowJo logo" />
          <ButtonWrapper>
            <FjButton
              onClick={() => {
                loadFJU('/flowjo');
              }}
              {...buttonProps}
            >
              Start Here
            </FjButton>
          </ButtonWrapper>
        </Panel>
        <Panel>
          <SeqGeqLogoContainer src={seqgeqLogo} alt="SeqGeq logo" />
          <ButtonWrapper>
            <FjButton
              onClick={() => {
                loadFJU('/seqgeq');
              }}
              {...buttonProps}
            >
              Start Here
            </FjButton>
          </ButtonWrapper>
        </Panel>
      </PanelContainer>
    </Container>
  );
};

export default withTheme(FJUDrawer);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
`;

const HeaderImage = styled.img`
  width: 350px;
`;

const SubHeader = styled.h2`
  color: ${(props) => props.theme.colors.productTitle};
  margin-top: 9px;
`;

const Text = styled.p`
  font-size: 15px;
  text-align: center;
  max-width: 530px;
`;

const UpperText = styled(Text)`
  font-size: 16px;
`;

const MainImage = styled.img`
  width: 400px;
  margin-top: 5px;
  margin-bottom: 8px;
`;

const PanelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 5px;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding-top: 45px;
  padding-bottom: 20px;
  height: 150px;
  width: 200px;
  border: solid 1px #ddd;
  border-radius: 2px;
`;

const PanelLogo = styled.img`
  transform: translateY(-50%);
`;
const FlowJoLogoContainer = styled(PanelLogo)`
  width: 125px;
`;
const SeqGeqLogoContainer = styled(PanelLogo)`
  width: 135px;
`;
const ButtonWrapper = styled.div`
  button {
    font-size: ${(props) => props.theme.fontSizes.buttonLarge};
  }
`;
